import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  newThread,
  listThreads,
  sendMessage,
  getHistory,
  deleteThread,
} from "../../services/api";
import Sidebar from "./Sidebar";
import Message from "./Message";
import ProgressPanel from "./progresspanel";
import {
  Paperclip, X, Send,
  Sparkles,
  ArrowLeft, ArrowRight, FileSpreadsheet, Sun, Moon, LogOut,
  Info
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ToastContainer from "./ToastContainer";
import Button from "../other/Button";
import CommandPalette from "../other/CommandPalette";
import AuroraBackground from "../other/AuroraBackground";

const SAMPLE_BILL_URL = `${process.env.PUBLIC_URL || ""}/samples/sample-cloud-invoice.pdf`;
const SAMPLE_PROMPT =
  "Analyze this cloud bill and build my Google Cloud migration TCO with a service-by-service mapping and projected savings.";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const ChatInterface = ({ darkMode, setDarkMode }) => {
  const navigate = useNavigate();
  const { threadId: urlThreadId } = useParams();
  const location = useLocation();
  const goToThread = (threadId) =>
    navigate(threadId ? `/chat/${threadId}` : "/chat");

  const [canGoBack, setCanGoBack] = useState(false);
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [conversations, setConversations] = useState([]);
  const [currentThread, setCurrentThread] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [samplePending, setSamplePending] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);

  const placeholderMessages = [
    "Upload your cloud cost & usage report...",
    "Estimate our Google Cloud TCO for migration...",
    "Map my workloads to Google Cloud equivalents...",
    "How much could we save on Google Cloud?",
    "Generate a Google Cloud migration cost workbook...",
  ];
  const [placeholderText, setPlaceholderText] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  // TRACKERS FOR PARALLEL BACKGROUND STREAMS
  const currentThreadRef = useRef(currentThread);
  const activeStreamsRef = useRef({}); // Stores isolated state for ALL active streams

  useEffect(() => {
    currentThreadRef.current = currentThread;
  }, [currentThread]);

  const [isThreadsLoading, setIsThreadsLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const infoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setShowUploadInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const [toasts, setToasts] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setCurrentThread(urlThreadId || null);
  }, [urlThreadId]);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (!u) {
      setError("No user found. Please login.");
      return;
    }
    setUser(u);
    fetchThreads(u.user_id);
    setMessages([]); // clean slate until history loads
  }, []);

  // TRAFFIC COP: Handles UI transitions smoothly without brutal resets
  useEffect(() => {
    if (!user) return;
    if (currentThread) {
      const activeStream = activeStreamsRef.current[currentThread];
      if (activeStream) {
        // Sync instantly if a background stream is already running for this chat
        setMessages(activeStream.messages || []);
        setProgressSteps(activeStream.progressSteps || []);
        setIsLoading(true);
      } else {
        // Safe reset and history load
        setMessages([]);
        setProgressSteps([]);
        setIsLoading(false);
        fetchHistory(currentThread);
      }
    } else {
      // Clean slate for "New Chat"
      setMessages([]);
      setProgressSteps([]);
      setIsLoading(false);
    }
  }, [user, currentThread]);

  useEffect(() => {
    let charIndex = 0;
    const currentText = placeholderMessages[placeholderIndex];
    setPlaceholderText("");

    const typingInterval = setInterval(() => {
      charIndex++;
      setPlaceholderText(currentText.slice(0, charIndex));

      if (charIndex >= currentText.length) {
        clearInterval(typingInterval);
        setTimeout(() => {
          setPlaceholderIndex((prev) => (prev + 1) % placeholderMessages.length);
        }, 2000);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, [placeholderIndex]);

  const fetchThreads = async (userId) => {
    if (!userId) return;
    try {
      setIsThreadsLoading(true);
      const res = await listThreads(userId);
      const threadsData = res.data?.data || res.data || [];
      setConversations(Array.isArray(threadsData) ? threadsData : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching threads:", err);
      setConversations([]);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  const fetchHistory = async (threadId) => {
    if (!user || !threadId) return;
    try {
      setIsHistoryLoading(true);
      const res = await getHistory(user.user_id, threadId);
      const historyData = res.data?.data || res.data || [];

      if (Array.isArray(historyData)) {
        const sortedMessages = historyData.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );
        setMessages(sortedMessages);
      } else {
        setMessages([]);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching history:", err);
      setError("Failed to load message history");
      setMessages([]);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // Safe navigation that doesn't cause UI flickering
  const handleNewChat = () => {
    if (!user || !user.user_id) return;
    try {
      setSelectedFile(null);
      setNewMessage("");
      goToThread(null);
      setError(null);
    } catch (err) {
      console.error("Error resetting to new chat:", err);
      setError("Failed to start a new conversation");
    }
  };

  const handleSelectConversation = (threadId) => {
    if (threadId !== currentThread) {
      setSelectedFile(null);
      setNewMessage("");
      goToThread(threadId);
    }
  };

  useEffect(() => {
    setCanGoBack(!!(window.history.state && window.history.state.idx > 0));
  }, [location]);

  const handleBack = () => {
    if (canGoBack) navigate(-1);
  };

  const handleTrySample = async () => {
    if (isLoading || sampleLoading) return;
    try {
      setSampleLoading(true);
      const res = await fetch(SAMPLE_BILL_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], "sample-cloud-invoice.pdf", {
        type: "application/pdf",
      });
      setSelectedFile(file);
      setNewMessage(SAMPLE_PROMPT);
      setSamplePending(true);
    } catch (err) {
      console.error("Sample load error:", err);
      showToast("Couldn't load the sample bill. Please try again.", "error");
      setSampleLoading(false);
    }
  };

  useEffect(() => {
    if (samplePending && selectedFile) {
      setSamplePending(false);
      setSampleLoading(false);
      handleSendMessage({ preventDefault: () => { } });
    }
  }, [samplePending, selectedFile]);

  // ChatInterface.js
  const tempBotMessage = {
    content: "",
    sender: "bot",
    isTyping: true,
    hasStartedStreaming: false,
    timestamp: new Date().toISOString(),
    // Attach the selected file info immediately so the card renders without waiting for refresh:
    related_file: selectedFile
      ? {
        file_name: selectedFile.name,
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        local_url: URL.createObjectURL(selectedFile),
        gcs_path: null,
        signed_url: null,
      }
      : null,
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      showToast(
        "Only PDF files are supported — please upload an AWS invoice in PDF format.",
        "error"
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
    showToast(`${file.name} attached`, "success");
  };

  const handleRemoveFile = (showNotification = true) => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (showNotification) {
      showToast?.("Attachment removed", "error");
    }
  };

  const handleSendMessage = async (e, editOptions = null) => {
    e?.preventDefault?.();
    const isEdit = editOptions !== null;
    const messageText = (isEdit ? editOptions.text : newMessage).trim();
    if ((!messageText && !selectedFile) || isLoading) return;

    setIsLoading(true);
    if (!isEdit) setNewMessage("");

    let activeThreadId = currentThread;
    let isNewThread = false;

    // ISOLATED UPDATERS
    const updateActiveMessages = (updater) => {
      const streamObj = activeStreamsRef.current[activeThreadId];
      if (streamObj) {
        streamObj.messages = typeof updater === 'function' ? updater(streamObj.messages) : updater;
        if (currentThreadRef.current === activeThreadId) {
          setMessages(streamObj.messages);
        }
      }
    };

    const updateActiveProgress = (updater) => {
      const streamObj = activeStreamsRef.current[activeThreadId];
      if (streamObj) {
        streamObj.progressSteps = typeof updater === 'function' ? updater(streamObj.progressSteps) : updater;
        if (currentThreadRef.current === activeThreadId) {
          setProgressSteps(streamObj.progressSteps);
        }
      }
    };

    try {
      if (!activeThreadId) {
        const res = await newThread(user.user_id);
        const newThreadData = res.data?.data || res.data;
        if (newThreadData && newThreadData.thread_id) {
          activeThreadId = newThreadData.thread_id;
          isNewThread = true;
        } else {
          throw new Error("Failed to create new thread");
        }
      }

      const currentTimestamp = new Date().toISOString();
      const tempUserMessage = {
        content: messageText || `📌 **Attached File:** _${selectedFile?.name}_`,
        sender: "user",
        timestamp: currentTimestamp,
        has_attachment: !!selectedFile,
        attachment: selectedFile
          ? {
            file_name: selectedFile.name,
            file_type: selectedFile.type,
            local_url: URL.createObjectURL(selectedFile),
            gcs_path: null,
            signed_url: null,
          }
          : null,
      };

      const tempBotMessage = {
        content: "",
        sender: "bot",
        isTyping: true,
        hasStartedStreaming: false,
        timestamp: new Date().toISOString(),
      };

      const initialMessages = isEdit
        ? [...messages.slice(0, editOptions.index), tempUserMessage, tempBotMessage]
        : isNewThread
          ? [{
            content: "Hi, How can I assist you today?",
            sender: "bot",
            timestamp: currentTimestamp
          }, tempUserMessage, tempBotMessage]
          : [...messages, tempUserMessage, tempBotMessage];

      // Attach completely isolated text buffers and timers per thread
      activeStreamsRef.current[activeThreadId] = {
        messages: initialMessages,
        progressSteps: [],
        textBuffer: "",
        flushTimer: null
      };

      if (isNewThread) {
        goToThread(activeThreadId);
        await fetchThreads(user.user_id);
        await new Promise((resolve) => setTimeout(resolve, 50));
      } else {
        updateActiveMessages(initialMessages);
        updateActiveProgress([]);
      }

      const formData = new FormData();
      formData.append("thread_id", activeThreadId);
      formData.append("user_id", user.user_id);
      formData.append("message", messageText);
      formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
      if (selectedFile) formData.append("file", selectedFile);
      if (isEdit) formData.append("edit_index", String(editOptions.index));

      const response = await fetch(`${BACKEND_URL}/api/chat/send`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let workbookReceived = null;
      let buffer = "";
      let generatedFileData = null;
      let chartDataReceived = null;
      let streamCompleted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line || line.startsWith(":")) continue;

          if (line.startsWith("data: ")) {
            const jsonStr = line.slice(6);
            try {
              const data = JSON.parse(jsonStr);

              if (data.type === "progress") {
                updateActiveProgress((prev) => {
                  const existing = prev.findIndex((s) => s.node === data.node);
                  const newStep = {
                    node: data.node,
                    status: data.status,
                    count: data.count || 0,
                    timestamp: data.timestamp,
                  };
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = newStep;
                    return updated;
                  } else {
                    return [...prev, newStep];
                  }
                });
              }

              if (data.type === "token") {
                const streamObj = activeStreamsRef.current[activeThreadId];
                if (!streamObj) continue;

                streamObj.textBuffer += data.content;

                if (!streamObj.flushTimer) {
                  streamObj.flushTimer = setTimeout(() => {
                    updateActiveMessages((prev) => {
                      const msgs = [...prev];
                      const lastIdx = msgs.length - 1;
                      if (msgs[lastIdx]?.sender === "bot") {
                        msgs[lastIdx] = {
                          ...msgs[lastIdx],
                          content: streamObj.textBuffer,
                          isTyping: true,
                          hasStartedStreaming: true,
                        };
                      }
                      return msgs;
                    });
                    streamObj.flushTimer = null;
                  }, 40);
                }
              } else if (data.type === "workbook" || data.type === "pricing_data") {
                workbookReceived = data.content || data.data;
                updateActiveMessages((prev) => {
                  const newMsgs = [...prev];
                  const lastIdx = newMsgs.length - 1;
                  if (newMsgs[lastIdx]?.sender === "bot") {
                    newMsgs[lastIdx] = {
                      ...newMsgs[lastIdx],
                      pricing_data: workbookReceived,
                    };
                  }
                  return newMsgs;
                });
              } else if (data.type === "generated_report") {
                generatedFileData = data.content;
                updateActiveMessages((prev) => {
                  const newMsgs = [...prev];
                  const lastIdx = newMsgs.length - 1;
                  if (newMsgs[lastIdx]?.sender === "bot") {
                    newMsgs[lastIdx] = {
                      ...newMsgs[lastIdx],
                      generated_file: data.content,
                    };
                  }
                  return newMsgs;
                });
              } else if (data.type === "chart_data") {
                chartDataReceived = data.content;
                updateActiveMessages((prev) => {
                  const newMsgs = [...prev];
                  const lastIdx = newMsgs.length - 1;
                  if (newMsgs[lastIdx]?.sender === "bot") {
                    newMsgs[lastIdx] = {
                      ...newMsgs[lastIdx],
                      chart_data: data.content,
                    };
                  }
                  return newMsgs;
                });
              } else if (data.type === "complete") {
                streamCompleted = true;
                const streamObj = activeStreamsRef.current[activeThreadId];

                if (currentThreadRef.current === activeThreadId) {
                  showToast("Response generated", "success");
                }
                if (streamObj && streamObj.flushTimer) {
                  clearTimeout(streamObj.flushTimer);
                  streamObj.flushTimer = null;
                }
                updateActiveMessages((prev) => {
                  const msgs = [...prev];
                  const lastIdx = msgs.length - 1;
                  if (msgs[lastIdx]?.sender === "bot") {
                    msgs[lastIdx] = {
                      ...msgs[lastIdx],
                      content: streamObj ? streamObj.textBuffer : "",
                      isTyping: false,
                      hasStartedStreaming: true,
                    };
                  }
                  return msgs;
                });
              } else if (data.type === "error") {
                updateActiveMessages((prev) => {
                  const newMsgs = [...prev];
                  const lastIdx = newMsgs.length - 1;
                  if (newMsgs[lastIdx]?.sender === "bot") {
                    newMsgs[lastIdx] = {
                      ...newMsgs[lastIdx],
                      content: `Error: ${data.content}`,
                      type: "error",
                    };
                  }
                  return newMsgs;
                });
                break;
              }
            } catch (parseErr) {
              console.error("[STREAM] ❌ JSON Parse error:", parseErr);
            }
          }
        }
        if (streamCompleted) break;
      }

      // ChatInterface.js (around line 375)
      if (selectedFile) {
        await new Promise((resolve) => setTimeout(resolve, 800));

        if (currentThreadRef.current === activeThreadId) {
          try {
            const res = await getHistory(user.user_id, activeThreadId);
            const historyData = res.data?.data || res.data || [];

            if (Array.isArray(historyData) && historyData.length > 0) {
              const sortedMessages = historyData.sort(
                (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
              );

              // Update both the in-memory stream buffer and state
              if (activeStreamsRef.current[activeThreadId]) {
                activeStreamsRef.current[activeThreadId].messages = sortedMessages;
              }
              setMessages(sortedMessages);
            }
          } catch (err) {
            console.error("Error syncing history:", err);
          }
        }

        if (generatedFileData || chartDataReceived) {
          updateActiveMessages((prev) => {
            const newMsgs = [...prev];
            const lastBotIdx = newMsgs.findLastIndex((m) => m.sender === "bot");
            if (lastBotIdx >= 0) {
              const patch = { ...newMsgs[lastBotIdx] };
              if (generatedFileData && !patch.generated_file) {
                patch.generated_file = generatedFileData;
              }
              if (chartDataReceived && !patch.chart_data) {
                patch.chart_data = chartDataReceived;
              }
              newMsgs[lastBotIdx] = patch;
            }
            return newMsgs;
          });
        }
      }
      updateActiveProgress([]);
      await fetchThreads(user.user_id);

      if (currentThreadRef.current === activeThreadId) {
        handleRemoveFile(false);
      }

    } catch (err) {
      if (currentThreadRef.current === activeThreadId) {
        showToast("Failed to send message", "error");
      }
      updateActiveMessages((prev) => {
        const newMsgs = [...prev];
        const lastIdx = newMsgs.length - 1;
        if (newMsgs[lastIdx]?.sender === "bot") {
          newMsgs[lastIdx] = {
            ...newMsgs[lastIdx],
            content: `Error: ${err.message}`,
            type: "error",
          };
        }
        return newMsgs;
      });
    } finally {
      const streamObj = activeStreamsRef.current[activeThreadId];
      if (streamObj && streamObj.flushTimer) {
        clearTimeout(streamObj.flushTimer);
      }
      delete activeStreamsRef.current[activeThreadId];
      if (currentThreadRef.current === activeThreadId) {
        setIsLoading(false);
      }
    }
  };

  const MessageSkeleton = () => (
    <div className="animate-pulse p-6 space-y-5">
      <div className="h-4 w-32 rounded-full skeleton-blue"></div>
      <div className="space-y-3">
        <div className="h-4 w-full rounded-full skeleton-blue"></div>
        <div className="h-4 w-5/6 rounded-full skeleton-blue"></div>
        <div className="h-4 w-3/4 rounded-full skeleton-blue"></div>
      </div>
    </div>
  );

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.replace("/login");
  };
  const commands = [
    { label: "New Chat", action: handleNewChat },
    { label: "Upload Invoice", action: () => fileInputRef.current?.click() },
    {
      label: sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar",
      action: () => setSidebarCollapsed(!sidebarCollapsed),
    },
    { label: "Focus Message Box", action: () => messageInputRef.current?.focus() },
    { label: "Logout", action: handleLogout },
  ];

  const filteredCommands = commands.filter((cmd) =>
    cmd.label.toLowerCase().includes(commandQuery.toLowerCase())
  );
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div
      className={`relative h-screen overflow-hidden transition-all duration-300 ${darkMode
        ? "text-white bg-[#0F172A]"
        : "text-slate-900 bg-slate-100"
        }`}
    >
      <>
        {darkMode && (
          <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
            <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-cyan-200/35 blur-[140px]" />
            <div className="absolute top-1/3 left-1/4 w-[420px] h-[420px] rounded-full bg-sky-200/30 blur-[120px]" />
            <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] rounded-full bg-blue-200/30 blur-[140px]" />
          </div>
        )}
      </>
      <ToastContainer toasts={toasts} darkMode={darkMode} />

      <div className="relative z-10 flex h-screen">
        <Sidebar
          user={user}
          conversations={conversations}
          currentConversation={currentThread}
          onCreateNewChat={handleNewChat}
          onSetCurrentConversation={handleSelectConversation}
          refreshConversations={() => fetchThreads(user?.user_id)}
          isLoading={isLoading}
          isThreadsLoading={isThreadsLoading}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          mobileOpen={mobileSidebarOpen}
          setMobileOpen={setMobileSidebarOpen}
          setConversations={setConversations}
          darkMode={darkMode}
        />
        <CommandPalette
          open={commandOpen}
          onClose={() => setCommandOpen(false)}
          query={commandQuery}
          setQuery={setCommandQuery}
          commands={filteredCommands}
          selectedIndex={selectedIndex}
          onSelect={(cmd) => {
            cmd.action();
            setCommandOpen(false);
          }}
        />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Bar */}
          <div
            className={`h-14 flex items-center justify-between px-5 border-b ${darkMode
              ? "glass hairline"
              : "bg-white/80 backdrop-blur-xl border-slate-200"
              }`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                disabled={!canGoBack}
                aria-label="Go back"
                title={canGoBack ? "Go back" : "Nothing to go back to"}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${!canGoBack
                  ? darkMode
                    ? "bg-white/[0.03] border-white/[0.06] text-white/25 cursor-not-allowed"
                    : "bg-slate-100 border-slate-200 text-slate-300 cursor-not-allowed"
                  : darkMode
                    ? "bg-white/10 border-white/10 text-white hover:bg-white/20"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100 shadow-sm"
                  }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-400" />
              </span>

              <h2
                className={`text-sm font-semibold ${darkMode ? "text-white" : "text-slate-900"
                  }`}
              >
                {conversations.find((c) => c.thread_id === currentThread)?.title || (
                  <>
                    Cost<span className="text-gradient-google">IQ</span> Assistant
                  </>
                )}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${darkMode
                  ? "bg-white/10 border-white/10 text-white hover:bg-white/20"
                  : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100 shadow-sm"
                  }`}
              >
                {darkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleLogout}
                aria-label="Log out"
                title="Log out"
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${darkMode
                  ? "bg-white/10 border-white/10 text-red-300 hover:bg-red-500/20 hover:text-red-200"
                  : "bg-white border-slate-300 text-red-600 hover:bg-red-50 hover:border-red-300 shadow-sm"
                  }`}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Message Area */}
            <div className="flex-1 overflow-y-auto px-4 md:px-8 space-y-2 pt-0 pb-0 scrollbar-transparent">
              {isHistoryLoading && messages.length === 0 ? (
                <>
                  <MessageSkeleton />
                  <MessageSkeleton />
                </>
              ) : messages.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center justify-center text-center px-6 max-w-3xl mx-auto min-h-full py-10"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-5"
                  >
                    <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center animate-float shadow-glow-md ${darkMode ? "bg-accent-soft border border-white/10" : "bg-sky-100 border border-sky-200"}`}>
                      <div className={`absolute inset-0 rounded-3xl ${darkMode ? "glass-sheen" : ""}`} />
                      <Sparkles className={`w-9 h-9 ${darkMode ? "text-accent-300" : "text-sky-500"}`} />
                    </div>
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className={`text-4xl md:text-5xl font-bold mb-2 tracking-tight ${darkMode ? "text-white" : "text-slate-900"
                      }`}
                  >
                    Hello, {user?.name || user?.username || "there"}
                  </motion.h1>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.16, duration: 0.5 }}
                    className="mb-5"
                  >
                    <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2">
                      <span
                        className={`text-3xl md:text-4xl font-extrabold tracking-tight ${darkMode ? "text-white" : "text-slate-900"
                          }`}
                      >
                        Cost<span className="text-gradient-google">IQ</span>
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${darkMode
                          ? "glass text-accent-200"
                          : "bg-sky-100 border border-sky-200 text-sky-700"
                          }`}
                      >
                        by Cloud Ambassadors
                      </span>
                    </div>
                    <p
                      className={`mt-3 text-xs md:text-sm font-semibold tracking-[0.18em] uppercase ${darkMode ? "text-accent-300" : "text-sky-700"
                        }`}
                    >
                      AI-Powered Total Cost Optimization Platform
                    </p>
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22, duration: 0.5 }}
                    className={`max-w-2xl text-base md:text-lg leading-relaxed mb-8 ${darkMode ? "text-slate-400" : "text-slate-600"
                      }`}
                  >
                    Analyze your cloud environment and receive an AI-powered Google
                    Cloud TCO assessment with intelligent service mapping and
                    migration recommendations.
                  </motion.p>

                  <motion.button
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.26, duration: 0.5 }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={handleTrySample}
                    disabled={sampleLoading || isLoading}
                    className={`group relative w-full max-w-2xl flex items-center gap-4 rounded-2xl px-5 py-4 mb-5 transition-all ${darkMode
                      ? "glass-sheen glass-strong border border-accent-400/25 shadow-glow-sm hover:shadow-glow-md"
                      : "bg-white border border-slate-200 shadow-lg hover:shadow-xl"
                      }`}
                  >
                    <span className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center shadow-glow-sm ${darkMode ? "bg-accent-gradient text-[#04121F]" : "bg-sky-400 text-white"}`}>
                      {sampleLoading ? (
                        <span className={`w-5 h-5 border-2 rounded-full animate-spin ${darkMode ? "border-[#04121F]/40 border-t-[#04121F]" : "border-white/40 border-t-white"}`} />
                      ) : (
                        <FileSpreadsheet className="w-5 h-5" />
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block text-[15px] font-semibold ${darkMode ? "text-white" : "text-slate-900"
                          }`}
                      >
                        {sampleLoading
                          ? "Loading sample analysis…"
                          : "Try it with a sample cloud bill"}
                      </span>
                      <span
                        className={`block text-xs mt-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"
                          }`}
                      >
                        No cloud bill handy? Run a full Google Cloud TCO on a real sample invoice.
                      </span>
                    </span>
                    <ArrowRight className="shrink-0 w-5 h-5 text-accent-300 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </motion.div>
              ) : (
                messages.map((message, index) => (
                  <Message
                    key={index}
                    message={message}
                    index={index}
                    progressSteps={
                      index === messages.length - 1 ? progressSteps : []
                    }
                    showToast={showToast}
                    darkMode={darkMode}
                    onEditAndRerun={(editedText) =>
                      handleSendMessage(null, { index, text: editedText })
                    }
                    isSendDisabled={isLoading}
                  />
                ))
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="px-4 md:px-8 pt-2 pb-5 shrink-0">
              <div className="max-w-3xl mx-auto">
                {/* File Preview */}
                {selectedFile && !isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-2 flex items-center gap-2 rounded-xl px-3 py-2 w-fit ${darkMode ? "glass" : "bg-white border border-slate-200 shadow-sm"}`}
                  >
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${darkMode ? "bg-accent-400/12 border border-accent-400/25" : "bg-sky-50 border border-sky-100"}`}>
                      <Paperclip className={`w-3.5 h-3.5 ${darkMode ? "text-accent-300" : "text-sky-600"}`} />
                    </span>
                    <span className={`text-sm max-w-[240px] truncate ${darkMode ? "text-slate-200" : "text-slate-700 font-medium"}`}>
                      {selectedFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className={`ml-1 transition-colors ${darkMode ? "text-slate-400 hover:text-rose-400" : "text-slate-400 hover:text-rose-500"}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}

                <div className="flex items-end gap-3">
                  {/* Upload Invoice Button with Integrated Info Icon */}
                  <div className="relative shrink-0" ref={infoRef}>
                    <div
                      className={`h-16 flex items-center rounded-3xl border transition-all ${darkMode
                        ? "glass-sheen glass-strong border-white/10 text-slate-300"
                        : "bg-white border-slate-200 text-slate-700 shadow-sm"
                        }`}
                    >
                      {/* Main Clickable Upload Area */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading}
                        className={`h-full flex items-center gap-2 pl-5 pr-2.5 transition-colors disabled:opacity-50 ${darkMode ? "hover:text-white" : "hover:text-sky-600"
                          }`}
                      >
                        <Paperclip style={{ width: 18, height: 18 }} />
                        <span className="hidden sm:inline text-sm font-medium whitespace-nowrap">
                          Upload Invoice
                        </span>
                      </button>

                      {/* Integrated Info Trigger Icon */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUploadInfo(!showUploadInfo);
                        }}
                        aria-label="Upload Requirements Info"
                        className={`h-7 w-7 mr-3 rounded-full flex items-center justify-center transition-colors ${darkMode
                          ? "hover:bg-white/10 text-slate-400 hover:text-sky-400"
                          : "hover:bg-slate-100 text-slate-400 hover:text-sky-600"
                          }`}
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Floating Rectangular Pop-up Note */}
                    <AnimatePresence>
                      {showUploadInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          className={`absolute bottom-20 left-0 z-50 w-72 p-4 rounded-2xl border shadow-2xl ${darkMode
                            ? "bg-slate-900/95 border-slate-700/80 text-slate-200 backdrop-blur-xl shadow-black/60"
                            : "bg-white/95 border-slate-200 text-slate-800 backdrop-blur-xl shadow-slate-300/80"
                            }`}
                        >
                          <div className="flex items-start gap-2.5">
                            <Info className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
                            <div className="text-xs leading-relaxed space-y-1">
                              <p className="font-semibold text-sky-400">Upload Note</p>
                              <p>
                                Please upload an <strong>AWS invoice</strong> in <strong>PDF format</strong>.
                              </p>
                              <p className={`pt-1.5 border-t ${darkMode ? "border-slate-800 text-slate-400" : "border-slate-100 text-slate-500"}`}>
                                CostIQ currently supports AWS to GCP TCO assessments only.
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Prompt panel */}
                  <form onSubmit={handleSendMessage} className="flex-1 min-w-0">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="application/pdf,.pdf"
                    />

                    <div
                      className={`relative rounded-4xl overflow-hidden transition-all duration-200 ${darkMode
                        ? "glass-sheen glass-strong shadow-e2 focus-within:shadow-glow-md focus-within:hairline-strong"
                        : "bg-white border border-slate-200 shadow-lg focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100"
                        }`}
                    >
                      <div className="flex items-end gap-2 pl-5 pr-2.5 py-2">
                        <textarea
                          ref={messageInputRef}
                          value={newMessage}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage(e);
                            }
                          }}
                          onChange={(e) => {
                            setNewMessage(e.target.value);
                            e.target.style.height = "auto";
                            e.target.style.height = `${Math.min(
                              e.target.scrollHeight,
                              160
                            )}px`;
                          }}
                          placeholder={newMessage ? "" : placeholderText}
                          rows={1}
                          disabled={isLoading}
                          className={`flex-1 bg-transparent resize-none border-none outline-none text-[15px] leading-relaxed py-2.5 ${darkMode
                            ? "text-white placeholder:text-slate-500"
                            : "text-slate-900 placeholder:text-slate-500"
                            }`}
                          style={{ minHeight: "28px", maxHeight: "160px" }}
                        />
                        <Button darkMode={darkMode}
                          type="submit"
                          variant="fab"
                          size="fab"
                          loading={isLoading}
                          disabled={
                            (!newMessage.trim() && !selectedFile) || isLoading
                          }
                        >
                          {!isLoading && <Send className="w-5 h-5" />}
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
                <p className="text-center text-[11px] text-slate-600 mt-2.5">
                  CostIQ can make mistakes. Verify pricing before finalizing a migration decision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;